'use client'

import { useDraggable } from '@dnd-kit/core'
import { addDays, addHours, differenceInMinutes } from 'date-fns'
import { atom, useAtom } from 'jotai'
import type {
  CSSProperties,
  FC,
  KeyboardEventHandler,
  MouseEventHandler,
  ReactNode,
  RefObject,
} from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'

import { cn } from '@/lib/utils'
import { durationAsSecondsToString, format } from '@/utils'
import { EyeIcon, EyeOffIcon } from 'lucide-react'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from './context-menu'
import { Card } from './card'
import { isMobile } from 'react-device-detect'
import { useTranslate } from '@ur-solutions/react-hooks'

const draggingAtom = atom(false)
const scrollXAtom = atom(0)

export const useGanttDragging = () => useAtom(draggingAtom)
export const useGanttScrollX = () => useAtom(scrollXAtom)

export type GanttStatus = {
  id: string
  name: string
  color: string
  active?: boolean
}

export type GanttFeature = {
  id: string | null
  name: string | null
  startAt: Date
  endAt: Date
  duration?: number
  status?: GanttStatus
  group?: GanttStatus
  tooltipContent?: React.ReactNode
}

export type GanttMarkerProps = {
  id: string
  date: Date
  label: string
}

export type Range = 'daily'

export type TimelineData = Date[]

export type GanttContextProps = {
  zoom: number
  range: Range
  columnWidth: number
  sidebarWidth: number
  headerHeight: number
  datetimeStart: Date
  datetimeEnd: Date
  rowHeight: number
  placeholderLength: number
  timelineData: TimelineData
  ref: RefObject<HTMLDivElement | null> | null
  scrollToFeature?: (feature: GanttFeature) => void
}
const createInitialTimelineData = (datetimeStart: Date, datetimeEnd: Date) => {
  const data: TimelineData = []

  let currentDate = datetimeStart
  while (currentDate <= datetimeEnd) {
    data.push(currentDate)
    currentDate = addDays(currentDate, 1)
  }

  return data
}

const getOffset = (
  date: Date,
  timelineStartDate: Date,
  context: GanttContextProps
) => {
  const parsedColumnWidth = (context.columnWidth * context.zoom) / 100
  const diffInMinutes = differenceInMinutes(date, timelineStartDate)
  return parsedColumnWidth * (diffInMinutes / (60 * 24))
}

const getWidth = (startAt: Date, endAt: Date, context: GanttContextProps) => {
  const parsedColumnWidth = (context.columnWidth * context.zoom) / 100
  const diffInMinutes = differenceInMinutes(endAt, startAt)
  return parsedColumnWidth * (diffInMinutes / (60 * 24))
}

const GanttContext = createContext<GanttContextProps>({
  zoom: 100,
  range: 'daily',
  columnWidth: 50,
  headerHeight: 60,
  sidebarWidth: 300,
  rowHeight: 36,
  placeholderLength: 2,
  timelineData: [],
  ref: null,
  scrollToFeature: undefined,
  datetimeStart: new Date(),
  datetimeEnd: addDays(new Date(), 1),
})

export type GanttContentHeaderProps = {
  renderHeaderItem: (index: number) => ReactNode
  title: string
  columns: number
}

export const GanttContentHeader: FC<GanttContentHeaderProps> = ({
  title,
  columns,
  renderHeaderItem,
}) => {
  const id = useId()

  return (
    <div
      className="sticky top-0 z-20 grid shrink-0 bg-backdrop/90"
      style={{ height: 'var(--gantt-header-height)' }}
    >
      <div>
        <div
          className="sticky inline-flex whitespace-nowrap px-3 py-2 text-muted-foreground text-xs"
          style={{
            left: `calc(var(--gantt-sidebar-width))`,
            width: '70px',
          }}
        >
          <p>{title}</p>
        </div>
      </div>
      <div
        className="grid w-full"
        style={{
          gridTemplateColumns: `repeat(${columns}, var(--gantt-column-width))`,
        }}
      >
        {Array.from({ length: columns }).map((_, index) => (
          <div
            className="shrink-0 border-border/50 border-b py-1 text-center text-xs"
            key={`${id}-${index}`}
          >
            {renderHeaderItem(index)}
          </div>
        ))}
      </div>
    </div>
  )
}

const DailyHeader: FC = () => {
  const gantt = useContext(GanttContext)

  const hoursArrays = useMemo(() => {
    const hours: { [x: string]: Date[] } = {}
    let current = gantt.datetimeStart
    while (current <= gantt.datetimeEnd) {
      const day = format(current, 'dd.MM.yyyy')
      if (!hours[day]) {
        hours[day] = []
      }
      hours[day].push(current)
      current = addHours(current, 1)
    }
    return hours
  }, [gantt.datetimeEnd, gantt.datetimeStart])

  const totalColumns = Object.values(hoursArrays).flatMap(hours => hours).length
  return (
    <div className="relative flex flex-col">
      <div className="relative flex flex-row">
        {Object.entries(hoursArrays).map(([dayStr, hours]) => (
          <GanttContentHeader
            key={dayStr}
            columns={Math.max(hours.length, 0)}
            renderHeaderItem={index => {
              const hour = hours[index + 1]
              if (hour === undefined) {
                const backupHour = hours[hours.length - 1]
                return (
                  <div
                    className="flex items-center justify-center gap-1"
                    style={{
                      marginLeft: 'calc(var(--gantt-column-width))',
                      zIndex: 20000,
                    }}
                  >
                    <p className="text-muted-foreground">
                      {format(addHours(backupHour ?? new Date(), 1), 'HH:mm')}
                    </p>
                  </div>
                )
              }
              return (
                <div
                  className="flex items-center justify-center gap-1"
                  style={{
                    marginLeft: 'calc(var(--gantt-column-width))',
                    zIndex: 2000,
                  }}
                >
                  <p className="text-muted-foreground">
                    {format(hour, 'HH:mm')}
                  </p>
                </div>
              )
            }}
            title={dayStr}
          />
        ))}
      </div>
      <GanttColumns columns={totalColumns} />
    </div>
  )
}

export type GanttHeaderProps = {
  className?: string
}

export const GanttHeader: FC<GanttHeaderProps> = ({ className }) => {
  return (
    <div
      className={cn(
        '-space-x-px flex h-full w-max divide-x divide-border/50',
        className
      )}
    >
      <DailyHeader />
    </div>
  )
}

export type GanttSidebarItemProps = {
  feature: GanttFeature
  onSelectItem?: (id: string | null) => void
  className?: string
}

export const GanttSidebarItem: FC<GanttSidebarItemProps> = ({
  feature,
  onSelectItem,
  className,
}) => {
  const gantt = useContext(GanttContext)
  const duration = durationAsSecondsToString(
    (feature.duration ?? differenceInMinutes(feature.endAt, feature.startAt)) *
      60
  )

  const handleClick: MouseEventHandler<HTMLDivElement> = event => {
    if (event.target === event.currentTarget) {
      // Scroll to the feature in the timeline
      gantt.scrollToFeature?.(feature)
      // Call the original onSelectItem callback
      onSelectItem?.(feature.id)
    }
  }

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = event => {
    if (event.key === 'Enter') {
      // Scroll to the feature in the timeline
      gantt.scrollToFeature?.(feature)
      // Call the original onSelectItem callback
      onSelectItem?.(feature.id)
    }
  }

  return (
    <div
      className={cn(
        'relative flex items-center gap-2.5 p-2.5 text-xs hover:bg-secondary',
        className
      )}
      key={feature.id}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      // biome-ignore lint/a11y/useSemanticElements: "This is a clickable item"
      role="button"
      style={{
        height: 'var(--gantt-row-height)',
      }}
      tabIndex={0}
    >
      {/* <Checkbox onCheckedChange={handleCheck} className="shrink-0" /> */}
      <div className="pointer-events-none h-2 w-2 shrink-0 rounded-full" />
      <p className="pointer-events-none flex-1 truncate text-left font-medium">
        {feature.name}
      </p>
      <p className="pointer-events-none text-muted-foreground">{duration}</p>
    </div>
  )
}

export const GanttSidebarHeader: FC = () => (
  <div
    className="sticky top-0 z-10 flex shrink-0 items-end justify-between gap-2.5 border-border/50 border-b bg-backdrop/90 p-2.5 font-medium text-muted-foreground text-xs backdrop-blur-sm"
    style={{ height: 'var(--gantt-header-height)' }}
  >
    {/* <Checkbox className="shrink-0" /> */}
    <p className="flex-1 truncate text-left">Datatype</p>
    <p className="shrink-0">Sum</p>
  </div>
)

export type GanttSidebarGroupProps = {
  children: ReactNode
  name: string
  className?: string
}

export const GanttSidebarGroup: FC<GanttSidebarGroupProps> = ({
  children,
  name,
  className,
}) => (
  <div className={className}>
    <p
      className="w-full truncate p-2.5 text-left font-medium text-muted-foreground text-xs"
      style={{ height: 'var(--gantt-row-height)' }}
    >
      {name}
    </p>
    <div className="divide-y divide-border/50">{children}</div>
  </div>
)

export type GanttSidebarProps = {
  children: ReactNode
  compact?: boolean
  className?: string
}

export const GanttSidebar: FC<GanttSidebarProps> = ({
  children,
  className,
}) => (
  <div
    className={cn(
      'sticky left-0 z-30 h-max min-h-full overflow-clip border-border/50 border-r bg-background/90 backdrop-blur-md',
      className
    )}
    data-roadmap-ui="gantt-sidebar"
  >
    <GanttSidebarHeader />
    <div className="space-y-4">{children}</div>
  </div>
)

export type GanttColumnProps = {
  index: number
  isColumnSecondary?: (item: number) => boolean
}

export const GanttColumn: FC<GanttColumnProps> = ({
  index,
  isColumnSecondary,
}) => {
  return (
    <div
      className={cn(
        'group relative h-full overflow-hidden',
        isColumnSecondary?.(index) ? 'bg-secondary' : ''
      )}
    ></div>
  )
}

export type GanttColumnsProps = {
  columns: number
  isColumnSecondary?: (item: number) => boolean
}

export const GanttColumns: FC<GanttColumnsProps> = ({
  columns,
  isColumnSecondary,
}) => {
  const id = useId()

  return (
    <div
      className="divide grid h-full w-full divide-x divide-border/50"
      style={{
        gridTemplateColumns: `repeat(${columns}, var(--gantt-column-width))`,
      }}
    >
      {Array.from({ length: columns }).map((_, index) => (
        <GanttColumn
          index={index}
          isColumnSecondary={isColumnSecondary}
          key={`${id}-${index}`}
        />
      ))}
    </div>
  )
}

export type GanttFeatureDragHelperProps = {
  featureId: GanttFeature['id']
  direction: 'left' | 'right'
  date: Date | null
}

export const GanttFeatureDragHelper: FC<GanttFeatureDragHelperProps> = ({
  direction,
  featureId,
  date,
}) => {
  const [, setDragging] = useGanttDragging()
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `feature-drag-helper-${featureId}`,
  })

  const isPressed = Boolean(attributes['aria-pressed'])

  useEffect(() => setDragging(isPressed), [isPressed, setDragging])

  return (
    <div
      className={cn(
        'group -translate-y-1/2 !cursor-col-resize absolute top-1/2 z-[3] h-full w-6 rounded-md outline-none',
        direction === 'left' ? '-left-2.5' : '-right-2.5'
      )}
      ref={setNodeRef}
      {...attributes}
      {...listeners}
    >
      <div
        className={cn(
          '-translate-y-1/2 absolute top-1/2 h-[80%] w-1 rounded-sm bg-muted-foreground opacity-0 transition-all',
          direction === 'left' ? 'left-2.5' : 'right-2.5',
          direction === 'left' ? 'group-hover:left-0' : 'group-hover:right-0',
          isPressed && (direction === 'left' ? 'left-0' : 'right-0'),
          'group-hover:opacity-100',
          isPressed && 'opacity-100'
        )}
      />
      {date && (
        <div
          className={cn(
            '-translate-x-1/2 absolute top-10 hidden whitespace-nowrap rounded-lg border border-border/50 bg-background/90 px-2 py-1 text-foreground text-xs backdrop-blur-lg group-hover:block',
            isPressed && 'block'
          )}
        >
          {format(date, 'MMM dd, yyyy')}
        </div>
      )}
    </div>
  )
}

export type GanttFeatureItemCardProps = Pick<GanttFeature, 'status'> & {
  tooltipContent?: React.ReactNode
  children?: ReactNode
}

export const GanttFeatureItemCard: FC<GanttFeatureItemCardProps> = ({
  status = null,
  tooltipContent,
  children,
}) => {
  return (
    <Card
      tooltipContent={tooltipContent}
      className={cn(
        'h-full w-full bg-background py-2 text-xs',
        status ? 'hover:border-1 hover:border-primary' : ''
      )}
      style={{ backgroundColor: status ? status.color : 'white' }}
    >
      <div
        className={cn(
          'flex h-full w-full items-center justify-between gap-2 pl-2 text-left'
        )}
      >
        {children}
      </div>
    </Card>
  )
}

export type GanttFeatureItemProps = GanttFeature & {
  onMove?: (id: string, startDate: Date, endDate: Date | null) => void
  children?: ReactNode
  className?: string
}

export const GanttFeatureItem: FC<GanttFeatureItemProps> = ({
  onMove,
  children,
  className,
  ...feature
}) => {
  const gantt = useContext(GanttContext)
  const timelineStartDate = useMemo(
    () => gantt.datetimeStart,
    [gantt.datetimeStart]
  )
  const startAt = feature.startAt
  const endAt = feature.endAt

  // Memoize expensive calculations
  const width = useMemo(
    () => getWidth(startAt, endAt, gantt),
    [startAt, endAt, gantt]
  )
  const offset = useMemo(
    () => getOffset(startAt, timelineStartDate, gantt),
    [startAt, timelineStartDate, gantt]
  )

  return (
    <div
      className={cn('relative flex w-max min-w-full py-0.5', className)}
      style={{ height: 'var(--gantt-row-height)' }}
    >
      <div
        className="pointer-events-auto absolute top-0.5"
        style={{
          height: 'calc(var(--gantt-row-height) - 4px)',
          width: Math.round(width),
          left: Math.round(offset),
        }}
        data-offset={offset}
      >
        <GanttFeatureItemCard
          status={feature.status ?? feature.group}
          tooltipContent={feature.tooltipContent}
        >
          {children ?? (
            <p className="flex-1 truncate text-xs">{feature.name}</p>
          )}
        </GanttFeatureItemCard>
      </div>
    </div>
  )
}

export type GanttFeatureListGroupProps = {
  children: ReactNode
  className?: string
}

export const GanttFeatureListGroup: FC<GanttFeatureListGroupProps> = ({
  children,
  className,
}) => (
  <div className={className} style={{ paddingTop: 'var(--gantt-row-height)' }}>
    {children}
  </div>
)

export type GanttFeatureRowProps = {
  features: GanttFeature[]
  handleViewFeature: (feature: GanttFeature) => void
  className?: string
}

export const GanttFeatureRow: FC<GanttFeatureRowProps> = ({
  features,
  handleViewFeature,
  className,
}) => {
  const rowHeight = 36 // Base row height
  const translations = useTranslate({
    changeSalaryCalculation: 'gantt.change-salary-calculation',
  })

  return (
    <div
      className={cn('relative', className)}
      style={{
        height: `0px`,
        minHeight: 'var(--gantt-row-height)',
      }}
    >
      {features.map(feature => (
        <div
          className="absolute w-full"
          key={feature.id}
          style={{
            top: `0px`,
            height: `${rowHeight}px`,
          }}
        >
          <ContextMenu>
            <ContextMenuTrigger>
              <GanttFeatureItem {...feature}>
                <p className="flex-1 truncate text-xs">{feature.name}</p>
              </GanttFeatureItem>
            </ContextMenuTrigger>
            {feature.status && (
              <ContextMenuContent>
                <ContextMenuItem
                  className="flex items-center gap-2"
                  onClick={() => handleViewFeature(feature)}
                >
                  {!!feature.status?.active ? (
                    <EyeOffIcon className="text-foreground" size={16} />
                  ) : (
                    <EyeIcon className="text-foreground" size={16} />
                  )}
                  {translations.changeSalaryCalculation}
                </ContextMenuItem>
              </ContextMenuContent>
            )}
          </ContextMenu>
        </div>
      ))}
    </div>
  )
}

export type GanttFeatureListProps = {
  className?: string
  children: ReactNode
}

export const GanttFeatureList: FC<GanttFeatureListProps> = ({
  className,
  children,
}) => (
  <div
    className={cn('absolute top-0 left-0 h-full w-max space-y-4', className)}
    style={{ marginTop: 'var(--gantt-header-height)' }}
  >
    {children}
  </div>
)

export type GanttProviderProps = {
  datetimeStart?: Date
  datetimeEnd?: Date
  range?: Range
  zoom?: number
  onAddItem?: (date: Date) => void
  children: ReactNode
  className?: string
}

export const GanttProvider: FC<GanttProviderProps> = ({
  datetimeStart = new Date(),
  datetimeEnd = addDays(new Date(), 1),
  zoom = 100,
  range = 'daily',
  children,
  className,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const timelineData = createInitialTimelineData(datetimeStart, datetimeEnd)
  const [, setScrollX] = useGanttScrollX()
  const [sidebarWidth, setSidebarWidth] = useState(0)

  const headerHeight = 80
  const rowHeight = 36
  const columnWidth = 5000
  // Memoize CSS variables to prevent unnecessary re-renders
  const cssVariables = useMemo(
    () =>
      ({
        '--gantt-zoom': `${zoom}`,
        '--gantt-column-width': `${(zoom / 2400) * columnWidth}px`,
        '--gantt-header-height': `${headerHeight}px`,
        '--gantt-row-height': `${rowHeight}px`,
        '--gantt-sidebar-width': `${sidebarWidth}px`,
      }) as CSSProperties,
    [zoom, columnWidth, sidebarWidth]
  )

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft =
        scrollRef.current.scrollWidth / 2 - scrollRef.current.clientWidth / 2
      setScrollX(scrollRef.current.scrollLeft)
    }
  }, [setScrollX])

  // Update sidebar width when DOM is ready
  useEffect(() => {
    const updateSidebarWidth = () => {
      const sidebarElement = scrollRef.current?.querySelector(
        '[data-roadmap-ui="gantt-sidebar"]'
      )
      const newWidth = sidebarElement ? (isMobile ? 100 : 300) : 0
      setSidebarWidth(newWidth)
    }

    // Update immediately
    updateSidebarWidth()

    // Also update on resize or when children change
    const observer = new MutationObserver(updateSidebarWidth)
    if (scrollRef.current) {
      observer.observe(scrollRef.current, {
        childList: true,
        subtree: true,
      })
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  const scrollToFeature = useCallback(
    (feature: GanttFeature) => {
      const scrollElement = scrollRef.current
      if (!scrollElement) {
        return
      }

      // Calculate timeline start date from timelineData
      const timelineStartDate = timelineData[0] ?? new Date()

      // Calculate the horizontal offset for the feature's start date
      const offset = getOffset(feature.startAt, timelineStartDate, {
        zoom,
        range,
        columnWidth,
        sidebarWidth,
        headerHeight,
        datetimeStart,
        datetimeEnd,
        rowHeight,
        placeholderLength: 2,
        timelineData,
        ref: scrollRef,
      })

      // Scroll to align the feature's start with the right side of the sidebar
      const targetScrollLeft = Math.max(0, offset)

      scrollElement.scrollTo({
        left: targetScrollLeft,
        behavior: 'smooth',
      })
    },
    [
      timelineData,
      zoom,
      range,
      columnWidth,
      sidebarWidth,
      datetimeStart,
      datetimeEnd,
    ]
  )

  return (
    <GanttContext.Provider
      value={{
        zoom,
        range,
        headerHeight,
        columnWidth,
        sidebarWidth,
        datetimeStart,
        datetimeEnd,
        rowHeight,
        timelineData,
        placeholderLength: 2,
        ref: scrollRef,
        scrollToFeature,
      }}
    >
      <div
        className={cn(
          'gantt relative isolate grid h-full w-full flex-none select-none overflow-auto rounded-sm',
          range,
          className
        )}
        ref={scrollRef}
        style={{
          ...cssVariables,
          gridTemplateColumns: 'var(--gantt-sidebar-width) 1fr',
        }}
      >
        {children}
      </div>
    </GanttContext.Provider>
  )
}

export type GanttTimelineProps = {
  children: ReactNode
  className?: string
}

export const GanttTimeline: FC<GanttTimelineProps> = ({
  children,
  className,
}) => (
  <div
    className={cn(
      'relative flex h-full w-max flex-none overflow-clip bg-white',
      className
    )}
  >
    {children}
  </div>
)
